import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import { X, Minimize2, Maximize2, Volume2, VolumeX } from "lucide-react";
import { STATIONS } from "../config/stations";
import { markReturningListener } from "../lib/favorite-station";

const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [muted, setMuted] = useState(true); // videos need muted to autoplay
  const [nowPlaying, setNowPlaying] = useState(null);
  const [tvMinimized, setTvMinimized] = useState(false);
  const [tvStatus, setTvStatus] = useState("idle"); // idle | loading | playing | error
  const [tvError, setTvError] = useState(null);
  const [tvAnchor, setTvAnchor] = useState(null); // {top,left,width,height} — when set, video window docks here
  const nowPlayingTimer = useRef(null);

  const fetchNowPlaying = useCallback(async (stationId) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/now-playing/${stationId}`;
      const res = await fetch(url);
      if (res.ok) setNowPlaying(await res.json());
    } catch (e) {
      /* ignore */
    }
  }, []);

  const attachHls = useCallback((videoEl, src) => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setTvStatus("loading");
    setTvError(null);
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(src);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoEl.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        // eslint-disable-next-line no-console
        console.warn("[HLS]", data.type, data.details, data);
        if (data.fatal) {
          setTvStatus("error");
          setTvError(data.details || "Playback error");
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              break;
          }
        }
      });
      hlsRef.current = hls;
    } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      videoEl.src = src;
    } else {
      setTvStatus("error");
      setTvError("HLS not supported in this browser");
    }
  }, []);

  const play = useCallback(
    async (stationId) => {
      const station = STATIONS.find((s) => s.id === stationId);
      if (!station || !station.src) return;

      if (audioRef.current) audioRef.current.pause();
      if (videoRef.current) videoRef.current.pause();
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      setCurrent(station);
      setTvMinimized(false);

      // Initial fetch + polling every 15s
      setNowPlaying(null);
      fetchNowPlaying(station.id);
      if (nowPlayingTimer.current) clearInterval(nowPlayingTimer.current);
      nowPlayingTimer.current = setInterval(() => fetchNowPlaying(station.id), 15000);

      // Play synchronously inside the user gesture (iOS Safari blocks deferred play())
      if (station.kind === "audio" && audioRef.current) {
        const el = audioRef.current;
        el.src = station.src;
        el.muted = false;
        el.volume = volume;
        el.load();
        el.play()
          .then(() => {
            setPlaying(true);
            markReturningListener();
          })
          .catch(() => setPlaying(false));
      } else if (station.kind === "video" && videoRef.current) {
        const el = videoRef.current;
        attachHls(el, station.src);
        el.muted = true;
        setMuted(true);
        el.volume = volume;
        el.onplaying = () => setTvStatus("playing");
        el.play()
          .then(() => {
            setPlaying(true);
            markReturningListener();
          })
          .catch(() => setPlaying(false));
      }
    },
    [attachHls, volume]
  );

  const pause = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    if (videoRef.current) videoRef.current.pause();
    setPlaying(false);
  }, []);

  const resume = useCallback(() => {
    if (!current) return;
    const el = current.kind === "audio" ? audioRef.current : videoRef.current;
    if (el) el.play().then(() => setPlaying(true)).catch(() => {});
  }, [current]);

  const toggle = useCallback(() => {
    if (!current) return;
    if (playing) pause();
    else resume();
  }, [current, playing, pause, resume]);

  const stop = useCallback(() => {
    pause();
    setCurrent(null);
    setNowPlaying(null);
    if (nowPlayingTimer.current) {
      clearInterval(nowPlayingTimer.current);
      nowPlayingTimer.current = null;
    }
    if (audioRef.current) audioRef.current.src = "";
    if (videoRef.current) videoRef.current.removeAttribute("src");
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, [pause]);

  const setVolume = useCallback((v) => {
    setVolumeState(v);
    if (audioRef.current) audioRef.current.volume = v;
    if (videoRef.current) videoRef.current.volume = v;
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      if (nowPlayingTimer.current) clearInterval(nowPlayingTimer.current);
    };
  }, []);

  const value = {
    current,
    playing,
    volume,
    muted,
    nowPlaying,
    tvMinimized,
    setTvMinimized,
    tvStatus,
    tvError,
    tvAnchor,
    setTvAnchor,
    play,
    pause,
    resume,
    toggle,
    stop,
    setVolume,
    toggleMute,
    audioRef,
    videoRef,
    refreshNowPlaying: fetchNowPlaying,
  };

  const isTv = current?.kind === "video";

  return (
    <PlayerContext.Provider value={value}>
      {children}

      {/* Persistent audio element (never unmounts) */}
      <audio
        ref={audioRef}
        preload="none"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />

      {/* Persistent video window — always mounted so playback survives route changes */}
      <div
        data-testid="tv-window"
        style={
          tvAnchor
            ? {
                position: "fixed",
                top: tvAnchor.top,
                left: tvAnchor.left,
                width: tvAnchor.width,
                height: tvAnchor.height,
              }
            : undefined
        }
        className={`z-[60] shadow-[0_0_40px_rgba(0,0,0,0.7)] border border-gold/40 rounded-lg overflow-hidden bg-black transition-all duration-300
          ${isTv ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none scale-95"}
          ${tvAnchor ? "" : "fixed"}
          ${!tvAnchor && tvMinimized ? "bottom-24 right-6 w-64 h-40" : ""}
          ${!tvAnchor && !tvMinimized ? "bottom-24 right-6 w-[92vw] max-w-[520px] h-[64vw] max-h-[300px] md:w-[520px] md:h-[300px]" : ""}
        `}
      >
        <video
          ref={videoRef}
          playsInline
          autoPlay
          muted={muted}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full object-cover bg-black"
        />
        {/* Loading / error overlay */}
        {isTv && tvStatus !== "playing" && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-black/80 text-center px-4">
            {tvStatus === "error" ? (
              <div>
                <div className="font-condensed uppercase tracking-widest text-crimson-live text-xs mb-1">Stream Error</div>
                <div className="text-neutral-300 text-xs">{tvError || "Cannot play the live TV feed."}</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full border-2 border-gold border-t-transparent animate-spin" />
                <div className="font-condensed uppercase tracking-widest text-gold text-[10px]">Tuning In…</div>
              </div>
            )}
          </div>
        )}
        {/* Top overlay */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/80 to-transparent">
          <div className="flex items-center gap-2">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-crimson-live live-dot" />
            </span>
            <span className="font-condensed uppercase tracking-widest text-[10px] text-white">
              GFN Soul NYC TV · Live
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="tv-mute"
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              className="w-7 h-7 grid place-items-center rounded bg-black/60 hover:bg-black/80 text-white"
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            {!tvAnchor && (
              <button
                data-testid="tv-toggle-size"
                onClick={() => setTvMinimized((m) => !m)}
                aria-label={tvMinimized ? "Maximize" : "Minimize"}
                className="w-7 h-7 grid place-items-center rounded bg-black/60 hover:bg-black/80 text-white"
              >
                {tvMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
              </button>
            )}
            {!tvAnchor && (
              <button
                data-testid="tv-close"
                onClick={stop}
                aria-label="Close TV"
                className="w-7 h-7 grid place-items-center rounded bg-black/60 hover:bg-crimson text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {/* Muted hint */}
        {muted && (
          <button
            data-testid="tv-unmute-cta"
            onClick={toggleMute}
            className="absolute bottom-3 left-3 bg-gold text-black font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1.5 rounded-full hover:bg-gold-light transition-colors"
          >
            🔊 Tap for sound
          </button>
        )}
      </div>
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be inside PlayerProvider");
  return ctx;
};
